import type { PerformanceClinic } from '@/entities/performance/api/performance.api';
import { monthOf } from '@/entities/performance/lib/performance';
import { EmptyState } from '@/shared/components/StateViews';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useLoadMoreSentinel } from '@/shared/hooks/useLoadMoreSentinel';
import { cn } from '@/shared/lib/utils';
import { formatWon } from '@/shared/utils/format';
import { Loader2, Search } from 'lucide-react';

/** 행 오른쪽 값 — 확정 금액, 없으면 확인중(검수 중 이미지 있음) · 정산 전 */
const clinicAmount = (c: PerformanceClinic) => {
  if (c.hasSettlement)
    return { text: formatWon(c.confirmedAmount), pending: false };
  if (c.pendingImageCount > 0) return { text: '확인중', pending: true };
  return { text: '정산 전', pending: true };
};

/**
 * M-09 좌측 병의원 리스트 — 검색 · 정렬(예상↓ → 확정↓) · 페이징은 서버 (GET /monthly/{month}/clinics)
 * 목록 끝이 보이면 다음 페이지를 부른다.
 */
export const ClinicList = ({
  ym,
  clinics,
  totalCount,
  loading,
  keyword,
  onKeywordChange,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  selectedId,
  onSelect,
  className,
}: {
  ym: string;
  clinics: PerformanceClinic[];
  totalCount: number | undefined;
  loading: boolean;
  keyword: string;
  onKeywordChange: (keyword: string) => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => unknown;
  selectedId?: number;
  onSelect: (clinicId: number) => void;
  className?: string;
}) => {
  const sentinelRef = useLoadMoreSentinel({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  return (
    <aside
      className={cn(
        'flex flex-col self-start rounded-card bg-surface px-4 py-[18px] lg:sticky lg:top-6',
        className,
      )}
    >
      <p className="text-[12.5px] leading-[19px] font-medium text-[#8d8d8d]">
        {ym.slice(0, 4)}년 {monthOf(ym)}월 · 병의원{' '}
        {totalCount === undefined ? '' : `${totalCount}곳`}
      </p>
      <label className="mt-2.5 flex h-[38px] items-center justify-between gap-2 rounded-[9px] border border-[#e5e5e5] bg-[#f7f7f7] px-[13px]">
        <input
          type="search"
          value={keyword}
          onChange={(e) => onKeywordChange(e.target.value)}
          placeholder="병의원 검색"
          aria-label="병의원 검색"
          className="w-full min-w-0 bg-transparent text-[13px] leading-5 text-ink outline-none placeholder:text-[#a8a8a8]"
        />
        <Search className="size-3.5 shrink-0 text-[#a8a8a8]" aria-hidden />
      </label>
      <ul className="mt-2.5 flex flex-col gap-1 lg:max-h-[calc(100vh-200px)] lg:overflow-y-auto">
        {loading &&
          Array.from({ length: 6 }, (_, i) => (
            <li key={i}>
              <Skeleton className="h-[60px] w-full rounded-[9px]" />
            </li>
          ))}
        {!loading && clinics.length === 0 && (
          <li>
            <EmptyState
              message={keyword ? '검색 결과가 없어요.' : '병의원이 없어요.'}
              className="py-6"
            />
          </li>
        )}
        {clinics.map((c) => {
          const selected = c.clinicId === selectedId;
          const amount = clinicAmount(c);
          return (
            <li key={c.clinicId}>
              <button
                type="button"
                onClick={() => onSelect(c.clinicId)}
                aria-current={selected || undefined}
                className={cn(
                  'flex min-h-[60px] w-full flex-col gap-[5px] rounded-[9px] px-3 py-2.5 text-left',
                  selected
                    ? 'bg-[#f4f4f4] outline-2 -outline-offset-2 outline-ink'
                    : 'hover:bg-subtle',
                )}
              >
                <span
                  className={cn(
                    'w-full truncate text-[12.5px] leading-[19px] text-ink',
                    selected && 'font-bold',
                  )}
                >
                  {c.clinicName}
                </span>
                <span
                  className={cn(
                    'text-[11.5px] leading-[17px] font-medium',
                    amount.pending ? 'text-warning' : 'text-success',
                  )}
                >
                  {amount.text}
                </span>
              </button>
            </li>
          );
        })}
        {hasNextPage && (
          <li
            ref={sentinelRef}
            className="flex justify-center py-3"
            aria-hidden
          >
            {isFetchingNextPage && (
              <Loader2 className="size-4 animate-spin text-ink-7" />
            )}
          </li>
        )}
      </ul>
    </aside>
  );
};
